import React, { useCallback, useState, createContext, useContext } from 'react';
export interface Product {
  id: number;
  name: string;
  price: number;
  image: string;
}
export interface CartItem extends Product {
  quantity: number;
}
interface CartContextValue {
  items: CartItem[];
  isOpen: boolean;
  count: number;
  subtotal: number;
  openCart: () => void;
  closeCart: () => void;
  addItem: (product: Product) => void;
  removeItem: (id: number) => void;
  updateQuantity: (id: number, quantity: number) => void;
}
const CartContext = createContext<CartContextValue | undefined>(undefined);
export function CartProvider({
  children
}: {
  children: ReactNode;
}) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);
  const addItem = useCallback((product: Product) => {
    setItems(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => item.id === product.id ? {
          ...item,
          quantity: item.quantity + 1
        } : item);
      }
      return [...prev, {
        ...product,
        quantity: 1
      }];
    });
    setIsOpen(true);
  }, []);
  const removeItem = useCallback((id: number) => {
    setItems(prev => prev.filter(item => item.id !== id));
  }, []);
  const updateQuantity = useCallback((id: number, quantity: number) => {
    setItems(prev => quantity <= 0 ? prev.filter(item => item.id !== id) : prev.map(item => item.id === id ? {
      ...item,
      quantity
    } : item));
  }, []);
  const count = items.reduce((acc, item) => acc + item.quantity, 0);
  const subtotal = items.reduce((acc, item) => acc + item.price * item.quantity, 0);
  return <CartContext.Provider value={{
    items,
    isOpen,
    count,
    subtotal,
    openCart,
    closeCart,
    addItem,
    removeItem,
    updateQuantity
  }} data-id="element-268">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
}